/**
 * Bounded client-side retry for the player join chain (#67 — hardens FR-2.4/FR-2.6 and
 * extends NFR-4.2's auto-reconnect posture from the WS layer to the HTTP join chain).
 *
 * On a ≥2-task fleet the host and each player are separate browser contexts → separate
 * ALB sticky cookies → different Fargate tasks, so a player's `/join` (and the by-code
 * lookup that precedes it) routinely lands on a task that does NOT own the game while
 * ownership is still converging. The server deliberately answers such requests with
 * **explicitly-retryable transients** — `404` (the join code hasn't propagated to this
 * task yet: a read-your-write window), `409` (the owner lease isn't readable yet), and
 * `503 owner unreachable; retry` — expecting the client to retry. The browser previously
 * threw on the first transient, so `conn-status` never reached "Connected". This module
 * makes the client honor those signals with a small bounded backoff. A definitive error
 * (invalid code, a `404` that persists past the budget, a rejected identity token) still
 * surfaces promptly — retries only rescue the documented transient window, never a real
 * failure.
 */

import { ApiError } from './api-error';

export { ApiError } from './api-error';

/**
 * Statuses the server emits DURING cross-task convergence and marks retryable:
 * `404` (join code not yet visible on this task), `409` (owner lease not yet readable),
 * `503` (owner unreachable; retry).
 */
const RETRYABLE_STATUSES = new Set([404, 409, 503]);

/** True if `err` is a transient the join chain should retry within its budget. */
export function isRetryableJoinError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status !== undefined &&
    RETRYABLE_STATUSES.has(err.status)
  );
}

/**
 * After the bounded retries are exhausted, decide whether a FAILED **auto-rejoin**
 * should drop the persisted session. Keep it only when the owner is still converging
 * (`409`/`503`) — a transient must NEVER bounce a returning player to the join form
 * (they can retry on the next reload). Everything else — an invalid code, a `404` that
 * persisted past the budget (the game is really gone/reaped), or a non-HTTP error — is
 * definitive, so the stale handle is cleared and self-heals to the form.
 */
export function shouldClearSessionOnJoinFailure(err: unknown): boolean {
  if (err instanceof ApiError && (err.status === 409 || err.status === 503)) {
    return false;
  }
  return true;
}

/** Tuning + injection points for {@link withJoinRetry} (tests inject `sleep`). */
export interface JoinRetryOptions {
  /** Retries after the first attempt (default 5 → up to 6 attempts). */
  retries?: number;
  /** Base backoff before doubling (default 300ms). */
  baseDelayMs?: number;
  /** Cap on any single backoff (default 1500ms → ~5s total worst case). */
  maxDelayMs?: number;
  /** Injectable delay (tests pass a no-op / fake clock). */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `attempt`, retrying on a retryable transient ({@link isRetryableJoinError}) with
 * capped exponential backoff. A definitive error or exhausting the budget rethrows the
 * last error unchanged (so the caller's existing error handling is preserved).
 */
export async function withJoinRetry<T>(
  attempt: () => Promise<T>,
  opts: JoinRetryOptions = {},
): Promise<T> {
  const retries = opts.retries ?? 5;
  const baseDelayMs = opts.baseDelayMs ?? 300;
  const maxDelayMs = opts.maxDelayMs ?? 1500;
  const sleep = opts.sleep ?? defaultSleep;

  let lastErr: unknown;
  for (let attemptNo = 0; attemptNo <= retries; attemptNo += 1) {
    try {
      return await attempt();
    } catch (err) {
      lastErr = err;
      if (!isRetryableJoinError(err) || attemptNo === retries) throw err;
      await sleep(Math.min(baseDelayMs * 2 ** attemptNo, maxDelayMs));
    }
  }
  throw lastErr;
}
